/**
 * Helpers for trace blocks to access TraceScene and SceneObject from
 * a FlowGraphContext. These are stashed as global context variables
 * when the coordinator is set up.
 */

import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { SceneObject } from '../../../core/SceneObject';
import type { TraceScene } from '../../../core/TraceScene';
import { Logger } from '../../../utils/Logger';

export const TRACE_SCENE_VAR = "__traceScene";
export const CONTEXT_OBJECT_VAR = "__contextObject";

/** Stash trace references on a FlowGraphContext so blocks can access them. */
export function setTraceContext(
    context: FlowGraphContext,
    traceScene: TraceScene,
    contextObject: SceneObject,
): void {
    context._setGlobalContextVariable(TRACE_SCENE_VAR, traceScene);
    context._setGlobalContextVariable(CONTEXT_OBJECT_VAR, contextObject);
}

/**
 * Resolve a target path to a SceneObject. An empty path means the host (the
 * context object) — the authoring convention. A non-empty path that doesn't
 * resolve, such as one at a deleted object, returns null and the op does
 * nothing: acting on the host instead would switch its states or hide its
 * subtree for a reference that was never about it. A context with no scene to
 * resolve against returns null for a non-empty path too. Logged on each call.
 */
export function resolveTarget(context: FlowGraphContext, targetPath: string): SceneObject | null {
    const traceScene = context._getGlobalContextVariable(TRACE_SCENE_VAR, null) as TraceScene | null;
    const contextObject = context._getGlobalContextVariable(CONTEXT_OBJECT_VAR, null) as SceneObject | null;
    if (!targetPath) return contextObject;
    const resolved = traceScene?.resolveSceneObject(targetPath, contextObject ?? undefined) ?? null;
    if (!resolved) {
        // ERROR, not WARN: Logger.level defaults to ERROR, so a warn here never
        // reaches anyone, and a broken reference must not be invisible.
        Logger.error(`[resolveTarget] Target path "${targetPath}" could not be resolved — doing nothing`);
    }
    return resolved;
}
